import React, { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Upload, Trash2, Loader2, ImageOff, Pencil } from "lucide-react";
import { toast } from "sonner";
import { nextPhotoId } from "@/lib/photos";

const VIEW_LABELS = { W: "Wide", M: "Medium", C: "Close-up" };

export default function PhotoManager({ surveyId, photos, setPhotos }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewer, setViewer] = useState(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let current = [...photos];
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("photo_id", nextPhotoId(current));
        fd.append("area", "");
        fd.append("view", "");
        fd.append("notes", "");
        const rec = await api.uploadPhoto(surveyId, fd);
        current = [...current, rec];
        setPhotos(current);
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const saveMeta = async () => {
    try {
      const updated = await api.updatePhoto(editing.id, {
        photo_id: editing.photo_id, area: editing.area, view: editing.view, notes: editing.notes,
      });
      setPhotos(photos.map((p) => (p.id === updated.id ? updated : p)));
      setEditing(null);
      toast.success("Photo details saved");
    } catch { toast.error("Save failed"); }
  };

  const del = async (p) => {
    try {
      await api.deletePhoto(p.id);
      setPhotos(photos.filter((x) => x.id !== p.id));
      toast.success("Photo removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handleFiles(e.target.files)} />
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
        <Button data-testid="photo-capture-btn" onClick={() => cameraRef.current?.click()} disabled={uploading} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Take photo
        </Button>
        <Button data-testid="photo-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="gap-2 tap-scale">
          <Upload className="h-4 w-4" /> Upload
        </Button>
      </div>

      {photos.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border py-12 text-muted-foreground">
          <ImageOff className="h-8 w-8" />
          <p className="text-sm">No photos yet. Capture or upload to build the register.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((p) => (
            <div key={p.id} data-testid={`photo-card-${p.photo_id}`} className="group overflow-hidden rounded-xl border border-border bg-card card-hover">
              <button className="relative block aspect-square w-full overflow-hidden bg-secondary/40" onClick={() => setViewer(p)}>
                <img src={fileUrl(p.storage_path)} alt={p.area || p.photo_id} loading="lazy" className="h-full w-full object-cover" />
                <span className="absolute left-2 top-2 rounded-md bg-primary px-1.5 py-0.5 font-mono text-[10px] font-bold text-primary-foreground">{p.photo_id}</span>
                {p.view && <span className="absolute right-2 top-2 rounded-md bg-background/80 px-1.5 py-0.5 font-mono text-[10px] font-bold text-foreground">{p.view}</span>}
              </button>
              <div className="flex items-center justify-between gap-1 p-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-foreground">{p.area || "Untagged"}</p>
                  {p.notes && <p className="truncate text-[11px] text-muted-foreground">{p.notes}</p>}
                </div>
                <div className="flex shrink-0">
                  <Button data-testid={`photo-edit-${p.photo_id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing({ ...p })}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`photo-del-${p.photo_id}`} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => del(p)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-mono">{editing?.photo_id}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <img src={fileUrl(editing.storage_path)} alt="" className="max-h-56 w-full rounded-lg object-contain bg-secondary/40" />
              <div>
                <Label className="mb-1.5 block text-xs text-muted-foreground">Photo ID</Label>
                <Input data-testid="photo-edit-id" value={editing.photo_id || ""} onChange={(e) => setEditing({ ...editing, photo_id: e.target.value })} className="font-mono bg-secondary/40" />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs text-muted-foreground">Area / item</Label>
                <Input data-testid="photo-edit-area" value={editing.area || ""} onChange={(e) => setEditing({ ...editing, area: e.target.value })} className="bg-secondary/40" placeholder="e.g. FOH bar, StudioLive console" />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs text-muted-foreground">View</Label>
                <div className="flex gap-2">
                  {["W", "M", "C"].map((v) => (
                    <button key={v} data-testid={`photo-edit-view-${v}`} onClick={() => setEditing({ ...editing, view: editing.view === v ? "" : v })}
                      className={`flex-1 rounded-md border py-2 font-mono text-xs font-bold tap-scale ${editing.view === v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary/40 text-muted-foreground"}`}>
                      {v} · {VIEW_LABELS[v]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs text-muted-foreground">Notes</Label>
                <Textarea data-testid="photo-edit-notes" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="bg-secondary/40" />
              </div>
              <Button data-testid="photo-edit-save" onClick={saveMeta} className="w-full bg-primary text-primary-foreground tap-scale hover:bg-primary/90">Save details</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full viewer */}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-mono">{viewer?.photo_id} · {viewer?.area || "Untagged"}</DialogTitle></DialogHeader>
          {viewer && <img src={fileUrl(viewer.storage_path)} alt="" className="max-h-[70vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
